import { BuilderComponent, BuilderContent } from "@builder.io/react";
import { BuilderContent as BuilderContentType } from "@builder.io/sdk";
import { GetServerSidePropsResult } from "next";
import { useRouter } from "next/router";
import React, { useCallback } from "react";

import { STALE_IF_ERROR_PAGE_PATH } from "@/shared/constants";

import { parallel } from "@/utils/promises";
import { applyStaleIfErrorCacheHeader } from "@/utils/requests";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { locales } from "@/translations/config";

import { setBuilderVariationProperty } from "@/analytics/tracking";

import { init, resolveAsyncProps } from "@/builder";
import { getBuilderVariationAmplitudeData } from "@/builder/helpers";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import {
    getBuilderBannerForIndex,
    getBuilderMessages,
    getBuilderNavLinks,
    getBuilderPage,
} from "@/pages/api/builder/builder";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";

import RouteSwitchPage from "@/components/v2/page-renderer/RouteSwitchPage";
import { RouteSwitchPageProps } from "@/components/v2/page-renderer/types";

import { GetServerSideProps, PageProps } from "@/types/page";

import { getRouteSwitchServerSideProps } from "@/features/v2/pages/routeSwitchServerSideProps";

type OwnProps = {
    builderTargetUrlPath: string;
    builderContent: BuilderContentType | null;
    builderMessages: Array<BuilderContentType>;
    builderNavLinks: Array<BuilderContentType>;
    builderBanner: BuilderContentType | null;
    isIndexPage: boolean;
    siteExperience: "builder-v1";
};

type BuilderPageProps = OwnProps & PageProps;
type Props = BuilderPageProps | RouteSwitchPageProps;

type Params = {
    slug: string[];
};

init(BuilderModel.PAGE);
init(BuilderModel.MESSAGE);
init(BuilderModel.BANNER);

const getBuilderServerSideProps = async (
    ctx: Parameters<GetServerSideProps<Props, Params>>[0]
): Promise<GetServerSidePropsResult<Props>> => {
    const { locale, params, res } = ctx;
    const { slug = [] } = params;
    const url = `/${slug.join("/")}`;
    const isIndexPage = url === "/";

    const builderContent = await getBuilderPage(url, locale);
    if (!builderContent) {
        if (url === "/" && (locales as ReadonlyArray<string>).includes(locale)) {
            throw new Error(`Home page not found for locale=${locale}`);
        } else if (url === STALE_IF_ERROR_PAGE_PATH) {
            // TODO: after test this on staging and prod, remove this code
            throw new Error(`Error test page`);
        }

        return {
            notFound: true,
        };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        builderBanner: () => (isIndexPage ? getBuilderBannerForIndex(locale) : null),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    await resolveAsyncProps(builderContent, ctx);

    if (getBuilderVariationAmplitudeData(builderContent)?.builderVariation) {
        // If we have a page with A/B testing we want users always to hit the server
        applyStaleIfErrorCacheHeader(res);
    }

    return {
        props: {
            builderTargetUrlPath: url,
            locale,
            builderContent,
            isIndexPage,
            siteExperience: "builder-v1",
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

export const getServerSideProps: GetServerSideProps<Props, Params> = async (ctx) =>
    (await getRouteSwitchServerSideProps(ctx)) ?? getBuilderServerSideProps(ctx);

const BuilderIndexPage: React.FC<BuilderPageProps> = (props) => {
    const {
        builderTargetUrlPath,
        builderContent,
        builderMessages,
        builderBanner,
        builderNavLinks,
        locale,
        headerNavigationItems,
        footerLocalNavItems,
        footerRegionalNavItems,
        isIndexPage,
    } = props;

    // Forcing the router helps the page to render which seems to be a workaround:
    // https://github.com/vercel/next.js/discussions/22512#discussioncomment-1779505
    const router = useRouter();

    const logBuilderInfoToAmplitude = useCallback(
        (content) => {
            const builderVariationData = getBuilderVariationAmplitudeData(content);
            setBuilderVariationProperty(
                locale,
                builderTargetUrlPath,
                builderVariationData.builderVariation
            );
        },
        [locale, builderTargetUrlPath]
    );

    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderMessages={builderMessages}
            title={builderContent.data.title}
            description={builderContent.data.description}
            builderNavLinks={builderNavLinks}
            useOldStyles={builderContent.data.useOldStyles}
            preventIndexing={builderContent.data.preventIndexing}
            isIndexPage={isIndexPage}
        >
            <BuilderComponent
                model={BuilderModel.BANNER}
                content={builderBanner}
                key={`BuilderComponent-${BuilderModel.BANNER}-${router.asPath}`}
            />

            <BuilderContent
                model={BuilderModel.PAGE}
                content={builderContent}
                key={`BuilderContent-${BuilderModel.PAGE}-${router.asPath}`}
            >
                {(variant, loading, totalContent) => {
                    if (loading) return null;
                    logBuilderInfoToAmplitude(totalContent);

                    return (
                        <>
                            <BuilderComponent
                                model={BuilderModel.PAGE}
                                content={totalContent}
                                data={variant}
                                key={`BuilderComponent-${BuilderModel.PAGE}-${router.asPath}`}
                            />
                        </>
                    );
                }}
            </BuilderContent>
        </DefaultLayout>
    );
};

const IndexPage: React.FC<Props> = (props) =>
    props.siteExperience === "contentful-v2" ? (
        <RouteSwitchPage {...props} />
    ) : (
        <BuilderIndexPage {...props} />
    );

export default IndexPage;
